<template>
  <div>
    <div class="min-w-[220px] max-w-[220px] bg-primary-950 p-2 h-full relative" v-if="show_menu">
      <div class="absolute top-1 left-1 text-xs text-primary-200">
        {{ version }}
      </div>
      <div class="absolute right-1 top-1">
        <button
          class="bg-primary-900 border-0 text-white hover:bg-neutral-100 hover:text-black px-2 rounded transition-all"
          @click="show_menu = false">
          <i class="fa fa-chevron-left"></i>
        </button>
      </div>
      <div class="flex flex-col h-full">
        <div class="flex flex-row justify-center">
          <div class="p-3" v-if="$theme === 'csa'">
            <div class="bg-neutral-200 p-2 rounded">
              <img src="../../assets/csa.svg" class="w-full">
            </div>
          </div>
          <div class="bg-primary-950 rounded mt-2" v-else-if="$theme === 'sara'">
            <img src="../../assets/logo_sara.png" class="max-h-24">
          </div>
        </div>

        <div class="grow flex flex-col gap-1 overflow-auto text-center">
          <template
            v-for="section in mainMenu"
            :key="section.key">
            <div v-if="section.items.some(item => checkPermission(item))">
              <div
                class="cursor-pointer flex items-center justify-center bg-primary-900 hover:bg-primary-800 transition-all rounded p-1 text-white"
                @click="toggleSection(section.key)">
                <b class="grow text-sm mb-0">{{ section.title }}</b>
                <i class="fa" :class="isSectionVisible(section.key) ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
              </div>
              <div v-if="isSectionVisible(section.key)"
                   class="rounded-b border-t border-solid border-b-0 border-x-0 border-primary-950 flex flex-col gap-1">
                <template v-for="item in section.items"
                          :key="item.label">
                  <router-link
                    v-if="checkPermission(item)"
                    :to="item.to"
                    class="block text-white no-underline hover:font-bold first:mt-1 hover:bg-primary-200 transition-all hover:text-primary-950 mx-2 rounded leading-none py-1">
                    <i :class="item.icon + ' me-1'"></i> {{ item.label }}
                  </router-link>
                </template>
              </div>
            </div>
          </template>
        </div>
        <div class=" my-2">
          <div class="flex flex-row">
            <div class="grow flex flex-row gap-1">
              <router-link class="btn btn-sm btn-outline-light" to="/configuraciones"
                           v-if="$permisos.can('configuraciones.menu')">
                <i class="fa fa-cog"></i>
              </router-link>
              <router-link to="/mi_cuenta" class="btn btn-sm btn-outline-light"
                           v-tooltip.top="data.full_name">
                <i class="fa fa-user-circle"></i>
              </router-link>
            </div>
            <div>
              <button @click="logout" class="btn btn-sm btn-outline-light"
                      v-tooltip.top="'Cerrar sesión'">
                <i class="fa fa-sign-out"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="px-3 py-2">
          <img src="../../assets/dev_by_white.png" class="w-full">
        </div>
      </div>
    </div>
    <div style="min-width: 55px; max-width: 55px;" class="bg-primary-950 h-full p-2" v-else>
      <div class="flex flex-col h-full">
        <div class="rounded p-1" v-if="$theme === 'csa'">
          <img src="../../assets/csa.svg" class="w-full">
        </div>
        <div class="rounded" v-else-if="$theme === 'sara'">
          <img src="../../assets/logo_sara.png" class="w-full">
        </div>
        <div class="grow flex items-center justify-end">
          <button class="btn btn-outline-light btn-sm float-end mb-2" style="height: fit-content !important;"
                  @click="show_menu = true">
            <i class="fa fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {inject, reactive, ref} from 'vue'
import {useStorage} from '@vueuse/core'
import axios from 'axios'
import {useUserStore} from '../../stores/user'
import {VERSION} from '../../code/app'
import {useRouter} from 'vue-router'

const userStore = useUserStore()
const router = useRouter()
const data = userStore.data
const version = VERSION

const show_menu = useStorage('sara.show_menu', true);
const $permisos = inject('permisos')

// Menu toggle states persisted in storage
const sectionStates = reactive({
  administracion: useStorage('sara.administracion', true),
  operacion: useStorage('sara.operacion', true),
  inventario: useStorage('sara.inventarios', true),
  monitoreo: useStorage('sara.monitoreo', true),
  contenido: useStorage('sara.contenido', true),
  mi_plan: useStorage('sara.mi_plan', true),
  estados_cuenta: useStorage('sara.estados_cuenta', true),
  cobranza: useStorage('sara.cobranza', true),
})

// Menu configuration
const mainMenu = [
  {
    key: 'administracion',
    title: 'Administración',
    items: [
      {
        label: 'Catálogos',
        icon: 'fa-solid fa-book',
        to: '/catalogos',
        // Use "some" to check multiple permissions
        permissionType: 'some',
        permission: [
          'cajas.menu',
          'categorias.menu',
          'estaciones.menu',
          'listasPrecios.menu',
          'motivosDevolucion.menu',
          'productos.menu',
          'unidadesMedida.menu',
          'usuarios.menu',
          'cupones.menu'
        ]
      },
      {
        label: 'Reportes',
        icon: 'fa-solid fa-chart-line',
        to: '/reportes',
        permissionType: 'can',
        permission: 'reportes.menu'
      }
    ]
  },
  {
    key: 'operacion',
    title: 'Operación',
    items: [
      {
        label: 'Registrar pedido',
        icon: 'fa-solid fa-cart-plus',
        to: '/pedidos',
        permissionType: 'can',
        permission: 'pedidos.menu'
      },
      {
        label: 'Pedidos y ventas',
        icon: 'fa-solid fa-list-check',
        to: '/estatusVentas',
        permissionType: 'can',
        permission: 'pedidos.menu'
      }
    ]
  },
  {
    key: 'estados_cuenta',
    title: 'Estados de cuenta',
    items: [
      {
        label: 'Cliente',
        icon: 'fa-solid fa-user',
        to: '/clientes',
        permissionType: 'can',
        permission: 'clientes.menu'
      },
      {
        label: 'Repartidores',
        icon: 'fa-solid fa-motorcycle',
        to: '/repartidores',
        permissionType: 'can',
        permission: 'repartidores.menu'
      },
      {
        label: 'Facturas globales',
        icon: 'fas fa-file-invoice-dollar',
        to: '/facturas_globales',
        permissionType: 'can',
        permission: 'facturas_globales.menu'
      }
    ]
  },
  {
    key: 'cobranza',
    title: 'Cobranza',
    items: [
      {
        label: 'Depósitos en general',
        icon: 'fa-solid fa-piggy-bank',
        to: '/depositosGeneral',
        permissionType: 'can',
        permission: 'depositos_general.menu'
      },
      {
        label: 'Cobranza de pedidos',
        icon: 'fa-solid fa-money-bill-wave',
        to: '/cobranzaPedidos',
        permissionType: 'can',
        permission: 'pedidos_cobranza.menu'
      },
    ]
  },
  {
    key: 'monitoreo',
    title: 'Supervisión',
    items: [
      {
        label: 'Monitor',
        icon: 'fa-solid fa-desktop',
        to: '/monitorPedidos',
        permissionType: 'can',
        permission: 'pedidos.menu'
      },
      {
        label: 'Cajas',
        icon: 'fa-solid fa-cash-register',
        to: '/cajasop',
        permissionType: 'can',
        permission: 'cajas_op.menu'
      },
      {
        label: 'Rastreo de unidades',
        icon: 'fa-solid fa-map-location-dot',
        to: '/monitorRastreo',
        permissionType: 'can',
        permission: 'pedidos.menu'
      }
    ]
  },
  {
    key: 'inventario',
    title: 'Inventario',
    items: [
      {
        label: 'Proveedores',
        icon: 'fa-solid fa-truck',
        to: '/proveedores',
        permissionType: 'can',
        permission: 'movimiento_almacen.menu'
      },
      {
        label: 'Entrada',
        icon: 'fa-solid fa-arrow-right-to-bracket',
        to: '/movimiento_almacen/entrada',
        permissionType: 'can',
        permission: 'movimiento_almacen.menu'
      },
      {
        label: 'Salida',
        icon: 'fa-solid fa-arrow-right-from-bracket',
        to: '/movimiento_almacen/salida',
        permissionType: 'can',
        permission: 'movimiento_almacen.menu'
      },
      {
        label: 'Kardex',
        icon: 'fa-solid fa-book',
        to: '/movimiento_almacen',
        permissionType: 'can',
        permission: 'movimiento_almacen.menu'
      },
      {
        label: 'Existencias',
        icon: 'fa-solid fa-boxes-stacked',
        to: '/existencias',
        permissionType: 'can',
        permission: 'movimiento_almacen.menu'
      },
      {
        label: 'Transformación',
        icon: 'fa-solid fa-wrench',
        to: '/transformaciones',
        permissionType: 'can',
        permission: 'transformaciones.menu'
      },
      {
        label: 'Preparación',
        icon: 'fa-solid fa-utensils',
        to: '/preparacion/vista',
        permissionType: 'can',
        permission: 'movimiento_almacen.menu'
      }
    ]
  },
  {
    key: 'contenido',
    title: 'Contenido',
    items: [
      {
        label: 'Pantallas',
        icon: 'fa-solid fa-tv',
        to: '/ContenidoMonitores',
        permissionType: 'can',
        permission: 'contenido.pantallas.list'
      },
      {
        label: 'Repositorio',
        icon: 'fa-solid fa-folder-open',
        to: '/ContenidoRepositorio',
        permissionType: 'can',
        permission: 'contenido.menu'
      }
    ]
  },
  {
    key: 'mi_plan',
    title: 'Mi plan',
    items: [
      {
        label: 'Pagos',
        icon: 'fa-solid fa-credit-card',
        to: '/PagosCarniceria',
        permissionType: 'can',
        permission: 'pagosCarniceria.menu'
      }
    ]
  }
]

// Toggle a section's visibility
const toggleSection = key => {
  sectionStates[key] = !sectionStates[key]
}

// Return if a section is visible
const isSectionVisible = key => sectionStates[key]

// Check an individual item permission
const checkPermission = item => {
  if (item.permissionType === 'can') {
    return $permisos.can(item.permission)
  }
  if (item.permissionType === 'some') {
    return $permisos.some(item.permission)
  }
  return false
}

const logout = async () => {
  await axios.post('/logout')
  userStore.data = null
  userStore.is_logged_in = null
  await router.replace('/login')
}
</script>
